import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '@environments/environment';

export const TENANT_PARAM = 't';

export type TenantResolveFailure = 'invalid' | 'not_configured' | 'network' | 'unknown_error';

const FAILURE_BY_CODE: Record<string, TenantResolveFailure> = {
  portal_tenant_handle_invalid: 'invalid',
  portal_access_not_configured: 'not_configured',
};

const STORAGE_KEY = 'pc_tenant';

interface StoredTenant {
  hash: string;
  companyId: string;
}

interface ApiEnvelope<T> {
  data: T;
}

interface ApiTenantResolution {
  companyId: string;
}

@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  private readonly tenantSignal = signal<StoredTenant | null>(this.readFromStorage());
  private readonly failureSignal = signal<TenantResolveFailure | null>(null);

  readonly resolveFailure = this.failureSignal.asReadonly();

  readonly tenant = this.tenantSignal.asReadonly();

  companyId(): string | null {
    return this.tenantSignal()?.companyId ?? null;
  }

  tenantHash(): string | null {
    return this.tenantSignal()?.hash ?? null;
  }

  resolve(): Observable<string | null> {
    const hash = new URLSearchParams(this.document.location.search).get(TENANT_PARAM)?.trim() ?? '';
    const stored = this.tenantSignal();

    if (!hash) {
      this.forget();
      return of(null);
    }
    if (stored?.hash === hash) return of(stored.companyId);

    return this.exchange(hash).pipe(
      tap((companyId) => {
        if (companyId) this.store({ hash, companyId });
        else this.forget();
      })
    );
  }

  tenantParam(): Record<string, string> {
    const hash = this.tenantHash();
    return hash ? { [TENANT_PARAM]: hash } : {};
  }

  /** Único punto de vuelta al login: reanexa el parámetro para que la URL siga siendo válida. */
  goToLogin(state?: Record<string, unknown>): void {
    this.router.navigate(['/login'], { queryParams: this.tenantParam(), state });
  }

  private exchange(hash: string): Observable<string | null> {
    return this.http
      .get<ApiEnvelope<ApiTenantResolution>>(`${environment.workflowApiUrl}/portal/client/resolve`, {
        params: { [TENANT_PARAM]: hash },
      })
      .pipe(
        map((response) => response.data?.companyId ?? null),
        catchError((error: HttpErrorResponse) => {
          this.failureSignal.set(
            error.status === 0 ? 'network' : FAILURE_BY_CODE[String(error.error?.code ?? '')] ?? 'unknown_error'
          );
          return of(null);
        })
      );
  }

  private forget(): void {
    this.tenantSignal.set(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }

  private store(tenant: StoredTenant): void {
    this.tenantSignal.set(tenant);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tenant));
  }

  private readFromStorage(): StoredTenant | null {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as StoredTenant;
      return parsed?.companyId ? parsed : null;
    } catch {
      return null;
    }
  }
}
